import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api";

export type BookStatus = "none" | "queued" | "polling" | "ready" | "failed" | "generating";
export type BookGateReason = "needs_purchase" | "needs_pro" | "quota_exhausted" | null;

export interface BookState {
  status: BookStatus;
  exportUrl: string | null;
  error: string | null;
  gateReason: BookGateReason;
  attempts: number;
  booksUsed: number | null;
  booksLimit: number | null;
  nextReset: string | null;
}

interface BookPollResponse {
  status: "none" | "queued" | "polling" | "ready" | "failed";
  export_url?: string;
  error?: string;
}

const POLL_INTERVAL_MS = 6000;
const MAX_ATTEMPTS = 40;

export function useBook(conversationId: string) {
  const [state, setState] = useState<BookState>({
    status: "none",
    exportUrl: null,
    error: null,
    gateReason: null,
    attempts: 0,
    booksUsed: null,
    booksLimit: null,
    nextReset: null,
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const pollOnce = useCallback(async () => {
    if (stoppedRef.current) return;
    try {
      const res = await apiFetch<BookPollResponse>(`/api/book/${conversationId}`, {
        method: "GET",
      });
      if (res.status === "ready" && res.export_url) {
        stoppedRef.current = true;
        clearTimer();
        setState((s) => ({ ...s, status: "ready", exportUrl: res.export_url ?? null, error: null }));
        return;
      }
      if (res.status === "failed") {
        stoppedRef.current = true;
        clearTimer();
        setState((s) => ({ ...s, status: "failed", error: res.error ?? "Génération interrompue" }));
        return;
      }
      attemptsRef.current += 1;
      setState((s) => ({ ...s, status: "generating", attempts: attemptsRef.current }));
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        stoppedRef.current = true;
        clearTimer();
        setState((s) => ({
          ...s,
          status: "failed",
          error: "Le dossier prend plus de temps que prévu. Réessayez dans un instant.",
        }));
        return;
      }
      timerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
    } catch (e) {
      stoppedRef.current = true;
      clearTimer();
      const msg = e instanceof Error ? e.message : "Erreur inconnue";
      setState((s) => ({ ...s, status: "failed", error: msg }));
    }
  }, [conversationId]);

  const generate = useCallback(async () => {
    stoppedRef.current = false;
    attemptsRef.current = 0;
    setState((s) => ({ ...s, status: "generating", exportUrl: null, error: null, gateReason: null, attempts: 0 }));
    try {
      await apiFetch(`/api/book/${conversationId}`, { method: "POST" });
      timerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        const payload = (e.payload ?? {}) as {
          reason?: BookGateReason;
          books_used?: number;
          books_limit?: number;
          next_reset?: string;
        };
        setState((s) => ({
          ...s,
          status: "none",
          exportUrl: null,
          error: null,
          gateReason: payload.reason ?? "needs_purchase",
          attempts: 0,
          booksUsed: payload.books_used ?? s.booksUsed,
          booksLimit: payload.books_limit ?? s.booksLimit,
          nextReset: payload.next_reset ?? null,
        }));
        return;
      }
      const msg = e instanceof Error ? e.message : "Erreur inconnue";
      setState((s) => ({ ...s, status: "failed", error: msg }));
    }
  }, [conversationId, pollOnce]);

  const checkStatus = useCallback(async () => {
    try {
      const res = await apiFetch<BookPollResponse>(`/api/book/${conversationId}`, {
        method: "GET",
      });
      if (res.status === "ready" && res.export_url) {
        setState((s) => ({ ...s, status: "ready", exportUrl: res.export_url ?? null }));
      } else if (res.status === "queued" || res.status === "polling") {
        stoppedRef.current = false;
        attemptsRef.current = 0;
        setState((s) => ({ ...s, status: "generating" }));
        timerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
      } else if (res.status === "failed") {
        setState((s) => ({ ...s, status: "failed", error: res.error ?? null }));
      }
    } catch {
      /* silencieux : l'utilisateur peut lancer manuellement */
    }
  }, [conversationId, pollOnce]);

  const startCheckout = useCallback(async () => {
    try {
      const res = await apiFetch<{ url: string }>(`/api/billing/book/checkout`, {
        method: "POST",
        body: JSON.stringify({ conversationId }),
      });
      if (res.url) window.location.href = res.url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erreur inconnue";
      setState((s) => ({ ...s, status: s.status, error: msg }));
    }
  }, [conversationId]);

  useEffect(() => {
    void checkStatus();
    return () => {
      stoppedRef.current = true;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  return { state, generate, startCheckout };
}
